/// <amd-module name="Build/Run/Components/IssueAnalysisPanel" />
import * as React from "react";

import {
    RunDetailsData,
    RunIssueData
} from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { NavConstants } from "Build/Common/Library/Constants";
import {
    TaskControllerView,
    ITaskControllerViewProps
} from "Build/Run/Components/TaskControllerView";
import * as Resources from "Build/Run/Resources";

import { ITfsPageService } from "Tfs/Platform/Page";

import { ObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { onClickFPS } from "VSS/Platform/FPS";
import { routeUrl } from "VSS/Platform/Location";

import { Button } from "VSSUI/Button";
import { IconSize, Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Panel, PanelFooter } from "VSSUI/Panel";
import {
    Table,
    ITableColumn,
    TableCell,
    TableHeaderCell,
    ITableRow
} from "VSSUI/Table";

export interface IIssueAnalysisPanelProps {
    issue: RunIssueData;
    run: RunDetailsData;
    onDismiss(): void;
    pageContext: IVssPageContext;
}

interface ITaskTableItem {
    /**
     * Primary text of the row that will be rendered in the list
     */
    primaryText: string;

    /**
     *  Props that will be passed to component
     */
    props: ITaskControllerViewProps;
}

export class IssueAnalysisPanel extends React.Component<IIssueAnalysisPanelProps> {
    private _taskItemProvider: ObservableArray<ITaskTableItem>;
    private _agentUrl: string;
    private _isHosted: boolean;

    constructor(props: IIssueAnalysisPanelProps) {
        super(props);

        this._taskItemProvider = new ObservableArray([
            {
                primaryText: format(
                    Resources.TaskName,
                    this.props.issue.taskType
                ),
                props: {
                    inputs: this.props.issue.inputs,
                    pageContext: this.props.pageContext,
                    taskName: this.props.issue.taskName,
                    taskType: this.props.issue.taskType,
                    taskVersion: this.props.issue.taskVersion,
                    onDismiss: this.props.onDismiss
                }
            }
        ]);

        const tfsPageService = this.props.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        this._agentUrl = hostedAgentLinks[this.props.issue.imageName];
        if (!this._agentUrl) {
            // try again but this time try to pass the image name through the table to normalize first
            // don't do this originally because the label to spec name table doesn't handle a few cases
            this._agentUrl = hostedAgentLinks[imageLabelToAgentSpecName[this.props.issue.imageName]];
        }
        if (this._agentUrl) {
            this._isHosted = true;
        }

        if (
            !this._agentUrl &&
            tfsPageData &&
            tfsPageData.project &&
            this.props.issue.queueId
        ) {
            this._agentUrl = routeUrl(
                this.props.pageContext,
                "ms.vss-build-web.run-pools-route",
                {
                    project: tfsPageData.project.id,
                    queueId: this.props.issue.queueId.toString()
                }
            );
        }
    }

    public render() {
        const columns: ITableColumn<ITaskTableItem>[] = [
            {
                id: Resources.ErrorAnalysis,
                className: "padding-horizontal-20",
                renderCell: this._renderTaskCell,
                width: -100,
                name: Resources.RunTimeDetails,
                renderHeaderCell: this._renderTaskHeader
            } as ITableColumn<ITaskTableItem>
        ];

        const descriptionText =
            this.props.issue.taskName && this.props.issue.taskType
                ? format(
                      "{0} ({1})",
                      this.props.issue.taskName,
                      this.props.issue.taskType
                  )
                : "";

        const issueMessage = this.props.issue.message.replace(/\\n/g, '\n');

        return (
            <Panel
                contentClassName="no-padding"
                titleProps={{
                    text: Resources.ErrorAnalysis,
                    className: "fontWeightSemiBold"
                }}
                description={descriptionText}
                onDismiss={this._onDismiss}
            >
                {this.props.issue.taskName && (
                    <div className="flex-column">
                        <span className="body-m padding-bottom-16 padding-horizontal-20 word-break">
                            <code>
                                {issueMessage}
                            </code>
                        </span>
                        <Table
                            columns={columns}
                            itemProvider={this._taskItemProvider}
                            role="table"
                            onSelect={this._onTaskSelected}
                        />

                        <PanelFooter>
                            <div className="flex-row">
                                {this._isHosted && (
                                    <Link href={this._agentUrl}>
                                        {Resources.AboutHostedImage}
                                    </Link>
                                )}
                                <div className="bolt-panel-footer-buttons flex-grow bolt-button-group flex-row rhythm-horizontal-8 flex-end">
                                    {this._agentUrl && !this._isHosted && (
                                        <Button
                                            text={Resources.ViewAgent}
                                            primary={false}
                                            href={this._agentUrl}
                                        />
                                    )}
                                    <Button
                                        text={Resources.ViewLog}
                                        primary={true}
                                        onClick={this._onLogsClick}
                                    />
                                </div>
                            </div>
                        </PanelFooter>
                    </div>
                )}
            </Panel>
        );
    }

    private _onLogsClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const jobRoutes: { [key: string]: string } = {};
        jobRoutes[NavConstants.job] = this.props.issue.jobId;
        if (this.props.issue.taskId) {
            jobRoutes[NavConstants.task] = this.props.issue.taskId;
        }
        if (this.props.issue.lineNumber) {
            jobRoutes[
                NavConstants.line
            ] = this.props.issue.lineNumber.toString();
        }
        const linkingService = this.props.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const jobUrl = linkingService.getBuildUrl(
            this.props.run.id,
            NavConstants.logs,
            undefined,
            undefined,
            jobRoutes
        );
        onClickFPS(this.props.pageContext, jobUrl, true, event);
    };

    private _onTaskSelected = (event: React.SyntheticEvent<HTMLElement, Event>, tableRow: ITableRow<ITaskTableItem>) => {
        if (tableRow.data.props.taskVersion && tableRow.data.props.taskType) {
            const layoutManager = this.props.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout(onDismiss => {
                return React.createElement(TaskControllerView, {
                    ...tableRow.data.props,
                    onDismiss: onDismiss
                });
            });
        }
    };

    private _renderTaskCell = (rowIndex: number, columnIndex: number, column: ITableColumn<ITaskTableItem>, item: ITaskTableItem) => {
        return (
            <TableCell key={"table-item" + rowIndex} columnIndex={columnIndex} className="issue-task-table-padding cursor-pointer">
                <div className="flex-grow flex-row">
                    <div className="flex-column flex-grow padding-vertical-8">
                        <span className="primary-text">{item.primaryText}</span>
                    </div>
                    {item.props.inputs && <Icon iconName="ChevronRightMed" size={IconSize.medium} />}
                </div>
            </TableCell>
        );
    };

    private _renderTaskHeader = (columnIndex: number, tableColumn: ITableColumn<ITaskTableItem>) => {
        return (
            <TableHeaderCell
                key={"table-header" + columnIndex}
                column={tableColumn}
                columnIndex={columnIndex}
            >
                <div className="text-ellipsis body-s">
                    <span>{tableColumn.name}</span>
                </div>
            </TableHeaderCell>
        );
    };

    private _onDismiss = () => {
        this.props.onDismiss();
    };
}

// While we have redirection logic in Distributed Task for image labels to agent specs
// it is possible that a job has an image label for its agent spec identifier. There is intentionally
// no map of these things that can be passed around from server to client, so we are creating this map manually.
//
// Keys are intentionally lower cased for lookup purposes
const imageLabelToAgentSpecName: { [key: string]: string } = {
    ["macos-1013"]: "macOS-10.13",
    ["macos 1013"]: "macOS-10.13",
    ["macos 10.13"]: "macOS-10.13",
    ["macos"]: "macOS-10.14",
    ["macos 10.14"]: "macOS-10.14",
    ["ubuntu16"]: "ubuntu-16.04",
    ["ubuntu 16.04"]: "ubuntu-16.04",
    ["defaulthosted"]: "vs2015-win2012r2",
    ["vs2015 win2012r2"]: "vs2015-win2012r2",
    ["vs2017"]: "vs2017-win2016",
    ["vs2017 win2016"]: "vs2017-win2016",
    ["wincon"]: "win1803",
    ["windows-2019-vs2019"]: "windows-2019",
    ["windows 2019 vs2019"]: "windows-2019",
    ["windows 2019"]: "windows-2019",
    ["mac_int1013"]: "appcenter-macos-10.13",
    ["appcenter macos 10.13"]: "appcenter-macos-10.13",
    ["mac_int"]: "appcenter-macos-10.14",
    ["appcenter macos 10.14"]: "appcenter-macos-10.14",
    ["xcode9-macos1012"]: "internal-macos-10.12",
    ["xcode9 macos1012"]: "internal-macos-10.12",
    ["internal macos 10.12"]: "internal-macos-10.12",
    ["mac_internal"]: "internal-macos-10.13",
    ["internal macos 10.13"]: "internal-macos-10.13",
    ["internal-macos1014"]: "internal-macos-10.14",
    ["internal macos1014"]: "internal-macos-10.14",
    ["internal macos 10.14"]: "internal-macos-10.14",
    ["ubuntu18"]: "ubuntu-18.04",
    ["ubuntu 18.04"]: "ubuntu-18.04"
};

const hostedAgentLinks: { [key: string]: string } = {
    "windows-latest":
        "https://aka.ms/windows-latest-readme",
    "windows-2022":
        "https://aka.ms/windows-2022-readme",
    "windows-2019":
        "https://aka.ms/windows-2019-readme",
    "ubuntu-latest":
        "https://aka.ms/ubuntu-latest-readme",
    "ubuntu-22.04":
        "https://aka.ms/ubuntu-22.04-readme",
    "ubuntu-20.04":
        "https://aka.ms/ubuntu-20.04-readme",
    "macOS-13":
        "https://aka.ms/macOS-13-readme",
    "macOS-latest":
        "https://aka.ms/macOS-latest-readme",
    "macOS-12":
        "https://aka.ms/macOS-12-readme",
    "macOS-11":
        "https://aka.ms/macOS-11-readme"
};
